import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Controls,
  Background,
  NodeTypes,
  Connection,
  addEdge,
  useNodesState,
  useEdgesState,
  BackgroundVariant,
} from 'reactflow';
import 'reactflow/dist/style.css';
import '../App.css'; // Import CSS to ensure consistent styling

import RoadmapNode, { RoadmapNodeData } from './RoadmapNode';
import { roadmapData, RoadmapChildNode } from '../data/roadmapData';
import Advertisement from './Advertisement';
import { trackPageView, trackSectionView } from '../utils/analytics';

// Define custom node types
const nodeTypes: NodeTypes = {
  roadmapNode: RoadmapNode,
};

const SectionRoadmap: React.FC = () => {
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  const [sectionData, setSectionData] = useState<RoadmapChildNode | null>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState<RoadmapNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Find the section data based on sectionId and track page view
  useEffect(() => {
    console.log("Section ID:", sectionId);
    if (sectionId) {
      const section = roadmapData.nodes.find(node => node.id === sectionId);
      console.log("Found section:", section);
      if (section) {
        setSectionData(section);
        const { nodes: sectionNodes, edges: sectionEdges } = createSectionFlowElements(section);
        setNodes(sectionNodes);
        setEdges(sectionEdges);
        
        // Track section page view
        trackPageView(`/section/${sectionId}`, `Section: ${section.label}`);
        
        // Track detailed section view
        trackSectionView(sectionId, section.label);
      }
    }
  }, [sectionId, setNodes, setEdges]);

  // Handle connections between nodes
  const onConnect = useCallback(
    (connection: Connection) => setEdges(eds => addEdge(connection, eds)),
    [setEdges]
  );

  // Function to create nodes and edges for a section
  const createSectionFlowElements = (section: RoadmapChildNode) => {
    const nodes: Node<RoadmapNodeData>[] = [];
    const edges: Edge[] = [];

    // Color schemes for section nodes - matching the cleaner main roadmap style
    const nodeColors = {
      main: 'linear-gradient(135deg, #3c64b1 0%, #5a76b3 100%)', // Professional blue
      child: 'linear-gradient(135deg, #4b6cb7 0%, #6487db 80%)', // Lighter blue
      subChild: 'linear-gradient(135deg, #5a76b3 0%, #8aa4d8 80%)', // Even lighter blue
      nextStep: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' // Green gradient for next step
    };

    // Layout settings
    const columnWidth = 300; // Width between columns
    const nodeVerticalSpacing = 180; // Vertical spacing between nodes
    const startY = 240; // Starting Y position for the first child node
    
    // Add the main section node
    nodes.push({
      id: section.id,
      type: 'roadmapNode',
      position: { x: 400, y: 80 },
      data: {
        label: section.label,
        description: section.description || 'מידע מפורט על שלב זה',
        tips: section.tips || [],
        color: 'transparent',
        gradient: nodeColors.main,
        isMain: true,
      },
    });

    // Note: Removed the back button node as it's now a floating button

    // Add child nodes
    if (section.children) {
      let lastChildY = 0; // Track the Y position of the last child node

      // Column-based layout for child nodes
      section.children.forEach((childNode, childIndex) => {
        // Calculate position based on index
        const x = 400; // Center position
        const y = startY + (childIndex * nodeVerticalSpacing);
        lastChildY = y; // Update the last child Y position
        
        nodes.push({
          id: childNode.id,
          type: 'roadmapNode',
          position: { x, y },
          data: {
            label: childNode.label,
            description: childNode.description || '',
            videoLink: childNode.videoLink,
            tips: childNode.tips || [],
            color: 'transparent',
            gradient: nodeColors.child,
            isChild: true,
            hasChildren: childNode.children && childNode.children.length > 0,
          },
        });

        // Connect main section node to this child
        edges.push({
          id: `e-${section.id}-${childNode.id}`,
          source: section.id,
          target: childNode.id,
          style: { stroke: '#888', strokeWidth: 2 },
        });

        // Connect with previous child node (if not first)
        if (childIndex > 0 && section.children) {
          const prevChildId = section.children[childIndex - 1].id;
          edges.push({
            id: `e-${prevChildId}-${childNode.id}`,
            source: prevChildId,
            target: childNode.id,
            style: { stroke: '#aaa', strokeWidth: 1.5 },
            animated: true,
          });
        }

        // Add sub-child nodes if they exist
        if (childNode.children && childNode.children.length > 0) {
          const subChildCount = childNode.children.length;
          
          childNode.children.forEach((subChild, subIndex) => {
            // Place sub-children to the right of their parent in a column
            const subX = x + columnWidth;
            const subY = y - ((subChildCount - 1) / 2 * (nodeVerticalSpacing * 0.7)) + (subIndex * (nodeVerticalSpacing * 0.7));
            
            nodes.push({
              id: subChild.id,
              type: 'roadmapNode',
              position: { x: subX, y: subY },
              data: {
                label: subChild.label,
                description: subChild.description || '',
                videoLink: subChild.videoLink,
                tips: subChild.tips || [],
                color: 'transparent',
                gradient: nodeColors.subChild,
                isSubChild: true,
              },
            });

            // Connect child to sub-child
            edges.push({
              id: `e-${childNode.id}-${subChild.id}`,
              source: childNode.id,
              target: subChild.id,
              style: { stroke: '#aaa', strokeWidth: 1.5 },
            });
            
            // Connect with previous sub-child node (if not first)
            if (subIndex > 0 && childNode.children) {
              const prevSubChildId = childNode.children[subIndex - 1].id;
              edges.push({
                id: `e-${prevSubChildId}-${subChild.id}`,
                source: prevSubChildId,
                target: subChild.id,
                style: { stroke: '#ccc', strokeWidth: 1 },
                animated: true,
              });
            }
          });
        }
      });

      // Add "Go to next step" node if there is a next section
      const currentSectionIndex = parseInt(section.id) - 1;
      if (currentSectionIndex < roadmapData.nodes.length - 1) {
        const nextSectionId = roadmapData.nodes[currentSectionIndex + 1].id;
        const nextSectionLabel = roadmapData.nodes[currentSectionIndex + 1].label;
        
        // Position the node below the last child
        const nextStepY = lastChildY + nodeVerticalSpacing;
        
        // Add the next step node
        nodes.push({
          id: `next-step-${nextSectionId}`,
          type: 'roadmapNode',
          position: { x: 400, y: nextStepY },
          data: {
            label: `עבור לשלב הבא: ${nextSectionLabel.split(' - ')[0] || nextSectionLabel}`,
            description: `מעבר לשלב הבא: ${nextSectionLabel}`,
            color: 'transparent',
            gradient: nodeColors.nextStep,
            isNextStepButton: true,
            nextSectionId: nextSectionId,
          },
        });
        
        // Connect the last child to the next step node
        if (section.children.length > 0) {
          const lastChildId = section.children[section.children.length - 1].id;
          edges.push({
            id: `e-${lastChildId}-next-step-${nextSectionId}`,
            source: lastChildId,
            target: `next-step-${nextSectionId}`,
            style: { stroke: '#4CAF50', strokeWidth: 2 },
            animated: true,
          });
        }
      }
    }

    return { nodes, edges };
  };

  // Handle node click
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {      
      // If next step button is clicked, navigate to the next section
      if (node.data?.isNextStepButton && node.data?.nextSectionId) {
        const nextSectionId = node.data.nextSectionId;
        const nextSection = roadmapData.nodes.find(node => node.id === nextSectionId);
        
        if (nextSection) {
          // Track section transition
          trackSectionView(nextSectionId, nextSection.label);
        }
        
        navigate(`/section/${nextSectionId}`);
        return;
      }
      
      // Prevent default selection
      event.stopPropagation();
    },
    [navigate]
  );

  // Handle back button click
  const handleBackClick = useCallback(() => {
    // Track returning to main view
    trackPageView('/', 'Job Search Roadmap - Main Page');
    navigate('/');
  }, [navigate]);

  if (!sectionData) {
    return <div className="loading">טוען...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <h1>{sectionData.label}</h1>
        <p>פירוט שלבים ומידע</p>
      </header>
      <Advertisement />
      <div className="section-roadmap-container" style={{ width: '100%', height: 'calc(100vh - 100px)', position: 'relative' }}>
        {/* Floating Back to Main Menu Button */}
        <button 
          className="floating-back-button"
          onClick={handleBackClick}
          aria-label="חזרה לתפריט הראשי"
        >
          <span className="back-icon">←</span>
          <span className="back-text">חזרה לתפריט הראשי</span>
        </button>

        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.4}
          maxZoom={1.5}
        >
          <Controls />
          <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        </ReactFlow>
      </div>
    </div>
  );
};

export default SectionRoadmap;